import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import api from '../../../api/axiosInterceptor';
import { API_BASE_URL } from '../../../config';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

// Hook to fetch FE Base list
const useFetchFeBaseList = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-base/`)
      .then(res => setList(res.data))
      .catch(() => setList([]));
  }, []);
  return list;
};

export const AdminFeDocsMasterComponent = () => {
  const feBaseList = useFetchFeBaseList();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form fields with value, error, helperText
  const [formFields, setFormFields] = useState({
    financial_establishment: { value: '', error: false, helperText: '' },
    fra: { value: null, error: false, helperText: '' },
    gst_in: { value: null, error: false, helperText: '' },
    e_kyc_doc: { value: null, error: false, helperText: '' },
    payar_benificiary_proof: { value: null, error: false, helperText: '' },
    e_kyc_doc_type: { value: '', error: false, helperText: '' },
  });

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'financial_establishment', field: 'financial_establishment', headerName: 'FE Base', width: 200 },
    { id: 'fra', field: 'fra', headerName: 'FRA', width: 120, renderCell: params => params.value ? <a href={params.value} target="_blank" rel="noopener noreferrer">View</a> : 'No File' },
    { id: 'gst_in', field: 'gst_in', headerName: 'GST IN', width: 120, renderCell: params => params.value ? <a href={params.value} target="_blank" rel="noopener noreferrer">View</a> : 'No File' },
    { id: 'e_kyc_doc', field: 'e_kyc_doc', headerName: 'E-KYC Doc', width: 120, renderCell: params => params.value ? <a href={params.value} target="_blank" rel="noopener noreferrer">View</a> : 'No File' },
    { id: 'payar_benificiary_proof', field: 'payar_benificiary_proof', headerName: 'Payar Beneficiary Proof', width: 170, renderCell: params => params.value ? <a href={params.value} target="_blank" rel="noopener noreferrer">View</a> : 'No File' },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: params => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={uploading}><Edit /></IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={uploading}><Delete /></IconButton>
        </>
      ),
    },
  ];

  const fetchDocs = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-docs/`)
      .then(response => {
        const apiData = response.data.map((item, index) => {
          const feBaseObj = feBaseList.find(fe => fe.id === (item.financial_establishment?.id || item.financial_establishment));
          return {
            id: item.id,
            slNo: index + 1,
            financial_establishment: feBaseObj ? feBaseObj.name_of_fe : (item.financial_establishment?.name_of_fe || item.financial_establishment),
            fra: item.fra,
            gst_in: item.gst_in,
            e_kyc_doc: item.e_kyc_doc,
            payar_benificiary_proof: item.payar_benificiary_proof,
          };
        });
        setRows(apiData);
        setLoading(false);
      })
      .catch(() => {
        setSnackbarMessage('Failed to load FE Docs.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (feBaseList.length > 0) fetchDocs();
  }, [feBaseList]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const resetFormFields = () => {
    setFormFields({
      financial_establishment: { value: '', error: false, helperText: '' },
      fra: { value: null, error: false, helperText: '' },
      gst_in: { value: null, error: false, helperText: '' },
      e_kyc_doc: { value: null, error: false, helperText: '' },
      payar_benificiary_proof: { value: null, error: false, helperText: '' },
      e_kyc_doc_type: { value: '', error: false, helperText: '' },
    });
    setUploadProgress(0);
  };

  const handleAddItem = () => {
    setIsEditMode(false);
    resetFormFields();
    setOpenFormDialog(true);
  };

  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);
    const feBaseObj = feBaseList.find(fe => fe.name_of_fe === item.financial_establishment || fe.id === item.financial_establishment);
    setFormFields({
      financial_establishment: { value: feBaseObj ? feBaseObj.id : '', error: false, helperText: '' },
      fra: { value: null, error: false, helperText: '' },
      gst_in: { value: null, error: false, helperText: '' },
      e_kyc_doc: { value: null, error: false, helperText: '' },
      payar_benificiary_proof: { value: null, error: false, helperText: '' },
      e_kyc_doc_type: { value: '', error: false, helperText: '' },
    });
    setUploadProgress(0);
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/fe-mgmt/fe-docs/${deleteItem.id}/`)
      .then(() => {
        setSnackbarMessage('Document deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchDocs();
      })
      .catch(() => {
        setSnackbarMessage('Failed to delete document.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteItem(null);
      });
  };

  const validateField = (name, value) => {
    if (name === 'financial_establishment' && !value) return 'FE Base is required';
    if (!isEditMode) {
      if (['fra', 'gst_in', 'e_kyc_doc', 'payar_benificiary_proof'].includes(name) && !value) return `${name.toUpperCase().replace(/_/g, ' ')} is required`;
    }
    return '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const errorMsg = validateField(name, value);
    setFormFields(prev => ({
      ...prev,
      [name]: { value, error: !!errorMsg, helperText: errorMsg },
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files.length > 0) {
      const errorMsg = validateField(name, files[0]);
      setFormFields(prev => ({
        ...prev,
        [name]: { value: files[0], error: !!errorMsg, helperText: errorMsg },
      }));
    }
  };

  const validateForm = () => {
    let valid = true;
    const newFields = { ...formFields };
    Object.keys(newFields).forEach(name => {
      const errMsg = validateField(name, newFields[name].value);
      if (errMsg) valid = false;
      newFields[name] = { ...newFields[name], error: !!errMsg, helperText: errMsg };
    });
    setFormFields(newFields);
    return valid;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;
    const payload = new FormData();
    payload.append('financial_establishment', formFields.financial_establishment.value);
    if (formFields.fra.value) payload.append('fra', formFields.fra.value);
    if (formFields.gst_in.value) payload.append('gst_in', formFields.gst_in.value);
    if (formFields.e_kyc_doc.value) payload.append('e_kyc_doc', formFields.e_kyc_doc.value);
    if (formFields.payar_benificiary_proof.value) payload.append('payar_benificiary_proof', formFields.payar_benificiary_proof.value);

    try {
      setUploading(true);
      setUploadProgress(0);
      if (isEditMode) {
        await api.patch(`/fe-mgmt/fe-docs/${selectedItem.id}/`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: e => setUploadProgress(Math.round((e.loaded * 100) / e.total)),
        });
        setSnackbarMessage('FE Docs updated successfully.');
      } else {
        await api.post(`/fe-mgmt/fe-docs/`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: e => setUploadProgress(Math.round((e.loaded * 100) / e.total)),
        });
        setSnackbarMessage('FE Docs added successfully.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchDocs();
      setOpenFormDialog(false);
    } catch {
      setSnackbarMessage('Failed to submit FE Docs.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (loading) return <LoadingSpinner size={30} />;

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">FE Docs</Typography>
        <Button variant="contained" color="primary" onClick={handleAddItem} disabled={uploading}>Add</Button>
      </Box>

      <TableComponent
        columns={columns}
        rows={rows}
        manageColumnStatus
        enableSort
        sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' } }}
      />

      <Dialog open={openFormDialog} onClose={() => !uploading && setOpenFormDialog(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>{isEditMode ? 'Edit FE Docs' : 'Add FE Docs'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            select
            margin="dense"
            label="FE Base"
            fullWidth
            name="financial_establishment"
            value={formFields.financial_establishment.value}
            error={formFields.financial_establishment.error}
            helperText={formFields.financial_establishment.helperText}
            onChange={handleInputChange}
            disabled={uploading}
          >
            {feBaseList.map(fe => (
              <MenuItem key={fe.id} value={fe.id}>{fe.name_of_fe || fe.id}</MenuItem>
            ))}
          </TextField>

          {['fra', 'gst_in', 'e_kyc_doc', 'payar_benificiary_proof'].map((field) => (
            <Box key={field} sx={{ mt: 2 }}>
              <Button variant="outlined" component="label" fullWidth sx={{ mb: 1 }}>
                {formFields[field].value ? formFields[field].value.name : (isEditMode && selectedItem?.[field] ? `${field.toUpperCase()} Document Uploaded` : `Upload ${field.replace(/_/g, ' ').toUpperCase()}`)}
                <input type="file" hidden name={field} onChange={handleFileChange} disabled={uploading} />
              </Button>
              {formFields[field].error && <Typography color="error" variant="body2">{formFields[field].helperText}</Typography>}
            </Box>
          ))}

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>Uploading... {uploadProgress}%</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => !uploading && setOpenFormDialog(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={uploading}>{isEditMode ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this FE Docs record?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={uploading}>Delete</Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};
